import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTransition from "../components/PageTransition";
import SearchForm from "../components/SearchForm";
import CompareForm from "../components/CompareForm";
import AnalysisHistory from "../components/AnalysisHistory";
import PushToggle from "../components/PushToggle";
import { useHistoryStore } from "../stores/historyStore";
import { useToastStore } from "../stores/toastStore";
import { useWatchlistStore } from "../stores/watchlistStore";
import ScoreBar from "../components/ScoreBar";
import ScoreSparkline from "../components/ScoreSparkline";
import type { PopularStock, MarketResponse, ScoreHistoryPoint } from "../types";
import { API_ENDPOINTS } from "../constants/api";
import { STORAGE_KEYS, SCORE_DELTA_ALERT_THRESHOLD } from "../constants/storage";
import { getWatchScoreHistory } from "../api";
import { syncPushWatchlist } from "../utils/push";

export default function HomePage() {
  const [mode, setMode] = useState<"analyze" | "compare">("analyze");
  const [popular, setPopular] = useState<PopularStock[]>([]);
  const [market, setMarket] = useState<MarketResponse>({});
  const [scoreHistory, setScoreHistory] = useState<Record<string, ScoreHistoryPoint[]>>({});
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { history, removeHistory } = useHistoryStore();
  const { toast } = useToastStore();
  const { watchlist, removeStock } = useWatchlistStore();

  // 누적 변동이 임계값(|score − baseScore| >= SCORE_DELTA_ALERT_THRESHOLD) 이상인 종목
  // baseScore 없는 기존 localStorage 항목은 자동 제외(graceful)
  const alertedStocks = useMemo(() => {
    return watchlist
      .filter(
        (s) =>
          s.baseScore != null &&
          s.score != null &&
          Math.abs(s.score - s.baseScore) >= SCORE_DELTA_ALERT_THRESHOLD,
      )
      .sort((a, b) => Math.abs(b.score! - b.baseScore!) - Math.abs(a.score! - a.baseScore!))
      .slice(0, 5);
  }, [watchlist]);

  useEffect(() => {
    fetch(API_ENDPOINTS.popular).then((r) => r.json()).then(setPopular).catch(() => {});
    fetch(API_ENDPOINTS.market).then((r) => r.json()).then(setMarket).catch(() => {});

    // 온보딩 (첫 방문 시)
    if (!localStorage.getItem(STORAGE_KEYS.onboarded)) {
      setTimeout(() => {
        toast.info("기업명을 검색하면 AI 재무분석 리포트를 받을 수 있습니다. '/' 키로 바로 검색!");
        localStorage.setItem(STORAGE_KEYS.onboarded, "1");
      }, 1000);
    }
    // 마운트 1회 실행 의도 — toast를 deps에 추가하면 store 참조 변경 시 fetch가 재실행됨
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 워치리스트 변경 시 이미 구독 중인 경우에만 서버 워치리스트 갱신
  // syncPushWatchlist 내부에서 기존 구독 존재 여부를 체크하므로 중복 구독 없음
  useEffect(() => {
    const codes = watchlist.map((w) => w.code);
    if (codes.length > 0) {
      syncPushWatchlist(codes).catch(() => {});
    }
  }, [watchlist]);

  // 워치 점수 히스토리 (Cycle 92): cron이 축적한 서버 점수 추이 — 카드 스파크라인용
  // codes 문자열 키로 비교해 watchlist 참조 변경만으로는 재요청하지 않음
  const watchCodesKey = watchlist.map((w) => w.code).join(",");
  useEffect(() => {
    if (!watchCodesKey) {
      setScoreHistory({});
      return;
    }
    getWatchScoreHistory(watchCodesKey.split(",")).then(setScoreHistory);
  }, [watchCodesKey]);

  const handleAnalyze = (company: string, years: number) => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "click_analyze", { company });
    }
    navigate(`/analyze/${encodeURIComponent(company)}?years=${years}`);
  };

  const handleCompareSubmit = (companies: string[], years: number) => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "click_compare", { companies: companies.join(",") });
    }
    const encoded = companies.map((c) => encodeURIComponent(c)).join(",");
    navigate(`/compare?companies=${encoded}&years=${years}`);
  };

  return (
    <PageTransition>
    <section role="banner" className="relative overflow-hidden bg-gradient-to-b from-indigo-50 via-white to-[#f5f6f8] dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-950 pt-20 pb-20 px-5 rounded-b-[60px] max-md:pt-10 max-md:pb-12 max-md:rounded-b-[30px]">
      <div className="max-w-[700px] mx-auto text-center relative z-1">
        <h1 className="text-[42px] font-extrabold leading-[1.3] mb-4 tracking-tight max-md:text-[22px] max-md:mb-2 text-zinc-950 dark:text-white">
          {t("home.title").split("\n").map((line, i) => (
            <span key={i}>{line}{i === 0 && <br />}</span>
          ))}
        </h1>
        <p className="text-zinc-500 dark:text-zinc-400 text-base leading-relaxed mb-10 max-md:text-sm max-md:mb-5">
          {t("home.subtitle").split("\n").map((line, i) => (
            <span key={i}>{line}{i === 0 && <br />}</span>
          ))}
        </p>

        {/* 탭 */}
        <div className="flex justify-center gap-1 mb-8 max-md:mb-5 bg-zinc-100 dark:bg-zinc-900/60 rounded-xl p-1 max-w-xs mx-auto">
          <button
            onClick={() => setMode("analyze")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors cursor-pointer ${mode === "analyze" ? "bg-indigo-500 text-white" : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"}`}
          >
            {t("common.analyze")}
          </button>
          <button
            onClick={() => setMode("compare")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors cursor-pointer ${mode === "compare" ? "bg-indigo-500 text-white" : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"}`}
          >
            {t("common.compare")}
          </button>
        </div>

        {mode === "analyze" ? (
          <>
            <SearchForm onSubmit={handleAnalyze} disabled={false} />
            <AnalysisHistory
              history={history}
              onAnalyze={handleAnalyze}
              onRemove={removeHistory}
            />
          </>
        ) : (
          <CompareForm onSubmit={handleCompareSubmit} disabled={false} onError={toast.error} />
        )}

        <div className="flex gap-2 justify-center flex-wrap max-md:hidden" role="list" aria-label={t("common.analyze")}>
          {["valuation", "profitability", "growth", "stability", "dividend"].map((key) => (
            <span key={key} role="listitem" className="px-3.5 py-1.5 rounded-full border border-indigo-200 dark:border-zinc-800 text-indigo-500 dark:text-zinc-400 text-[13px] font-medium">{t(`home.tags.${key}`)}</span>
          ))}
        </div>
      </div>
    </section>

    {/* 시장 지수 */}
    {(market.kospi || market.kosdaq) && (
    <section className="py-8 px-5 max-md:py-5">
      <div className="max-w-[900px] mx-auto flex gap-4 justify-center max-md:flex-col max-md:gap-3">
        {market.kospi && (
          <div className="flex items-center gap-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl px-6 py-4 shadow-sm max-md:px-4">
            <span className="text-sm font-semibold text-zinc-500 dark:text-zinc-400">KOSPI</span>
            <span className="text-xl font-bold text-zinc-950 dark:text-zinc-100">{market.kospi.value.toLocaleString()}</span>
            <span className={`text-sm font-semibold ${market.kospi.change >= 0 ? "text-red-500" : "text-blue-500"}`}>
              {market.kospi.change >= 0 ? "▲" : "▼"} {Math.abs(market.kospi.change).toFixed(2)} ({market.kospi.change >= 0 ? "+" : ""}{market.kospi.change_pct}%)
            </span>
          </div>
        )}
        {market.kosdaq && (
          <div className="flex items-center gap-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl px-6 py-4 shadow-sm max-md:px-4">
            <span className="text-sm font-semibold text-zinc-500 dark:text-zinc-400">KOSDAQ</span>
            <span className="text-xl font-bold text-zinc-950 dark:text-zinc-100">{market.kosdaq.value.toLocaleString()}</span>
            <span className={`text-sm font-semibold ${market.kosdaq.change >= 0 ? "text-red-500" : "text-blue-500"}`}>
              {market.kosdaq.change >= 0 ? "▲" : "▼"} {Math.abs(market.kosdaq.change).toFixed(2)} ({market.kosdaq.change >= 0 ? "+" : ""}{market.kosdaq.change_pct}%)
            </span>
          </div>
        )}
      </div>
    </section>
    )}

    {/* 숫자 통계 */}
    <section className="py-12 px-5 max-md:py-8">
      <div className="max-w-[900px] mx-auto grid grid-cols-4 gap-4 max-md:grid-cols-2 max-md:gap-3">
        {[
          { value: "3,916", label: "상장기업", icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/></svg> },
          { value: "6", label: "분석 카테고리", icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> },
          { value: "AI", label: "투자 코멘트", icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 0 1 4 4c0 1.95-1.4 3.58-3.25 3.93L12 22"/><path d="M12 2a4 4 0 0 0-4 4c0 1.95 1.4 3.58 3.25 3.93"/></svg> },
          { value: "0.4s", label: "캐시 재조회", icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg> },
        ].map((s) => (
          <div key={s.label} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-5 text-center shadow-sm max-md:p-4">
            <div className="flex justify-center mb-3">{s.icon}</div>
            <div className="text-2xl font-extrabold text-zinc-950 dark:text-zinc-100 max-md:text-xl">{s.value}</div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{s.label}</div>
          </div>
        ))}
      </div>
    </section>

    {/* 서비스 소개 3단계 */}
    <section className="py-12 px-5 max-md:py-8">
      <div className="max-w-[900px] mx-auto">
        <h2 className="text-2xl font-bold text-zinc-950 dark:text-zinc-100 text-center mb-10 max-md:text-lg max-md:mb-6">어떻게 동작하나요?</h2>
        <div className="grid grid-cols-3 gap-6 max-md:grid-cols-1 max-md:gap-4">
          {[
            {
              step: "1",
              title: "기업명 검색",
              desc: "분석하고 싶은 한국 상장기업의 이름을 입력하세요.",
              color: "bg-indigo-500",
              icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
            },
            {
              step: "2",
              title: "자동 분석",
              desc: "DART 재무제표, 주가, 배당 데이터를 자동 수집하고 6개 카테고리로 평가합니다.",
              color: "bg-green-500",
              icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M7 16l4-8 4 4 4-8"/></svg>,
            },
            {
              step: "3",
              title: "리포트 확인",
              desc: "차트, 점수, AI 코멘트가 포함된 종합 투자 리포트를 확인하세요.",
              color: "bg-amber-500",
              icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>,
            },
          ].map((item) => (
            <div key={item.step} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-6 shadow-sm max-md:flex max-md:gap-4 max-md:items-start max-md:p-4">
              <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mb-4 shrink-0 max-md:mb-0`}>
                {item.icon}
              </div>
              <div>
                <div className="text-xs text-zinc-400 font-semibold mb-1">STEP {item.step}</div>
                <h3 className="text-base font-bold text-zinc-950 dark:text-zinc-100 mb-2">{item.title}</h3>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* 분석 예시 */}
    <section className="py-12 px-5 max-md:py-8">
      <div className="max-w-[900px] mx-auto">
        <h2 className="text-2xl font-bold text-zinc-950 dark:text-zinc-100 text-center mb-3 max-md:text-lg">
          분석 리포트 예시
        </h2>
        <p className="text-zinc-500 dark:text-zinc-400 text-sm text-center mb-8 max-md:mb-5">
          이수빈주식회사의 분석 결과 미리보기
        </p>
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-7 shadow-sm max-md:p-5 max-md:rounded-xl">
          <div className="flex items-center justify-between mb-5 max-md:flex-col max-md:items-start max-md:gap-2">
            <div>
              <h3 className="text-lg font-bold text-zinc-950 dark:text-zinc-100">이수빈주식회사</h3>
              <span className="text-sm text-zinc-400">000000 · KOSPI · 52,300원 · 시총 3조 2,100억</span>
            </div>
            <div className="text-right max-md:text-left">
              <div className="text-3xl font-extrabold text-indigo-500">72점</div>
              <div className="text-sm text-zinc-500">Buy - 긍정적, 일부 리스크 존재</div>
            </div>
          </div>
          <ScoreBar scores={[
            { key: "valuation", label: "밸류에이션", score: 75, detail: "" },
            { key: "profitability", label: "수익성", score: 80, detail: "" },
            { key: "growth", label: "성장성", score: 65, detail: "" },
            { key: "stability", label: "안정성", score: 78, detail: "" },
            { key: "dividend", label: "배당", score: 55, detail: "" },
            { key: "cashflow", label: "현금흐름", score: 70, detail: "" },
          ]} />
          <p className="text-[13px] text-zinc-500 dark:text-zinc-400 leading-relaxed mt-5 line-clamp-3">
            PER 9.2배는 업종 평균 대비 합리적인 수준이며, ROE 15.3%로 자본 효율성이 우수합니다. 영업이익률 12.8%와 FCF 마진 8.5%는 안정적인 현금 창출력을 보여주고 있으며, 부채비율 62.1%로 재무 건전성도 양호합니다.
          </p>
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              setTimeout(() => {
                const input = document.querySelector<HTMLInputElement>("input[type='text']");
                input?.focus();
              }, 500);
            }}
            className="mt-5 w-full py-3 bg-indigo-500 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-indigo-600 transition-colors"
          >
            직접 분석해보기
          </button>
        </div>
      </div>
    </section>

    {/* 관심종목 */}
    {watchlist.length > 0 && (
    <section className="py-12 px-5 max-md:py-8">
      <div className="max-w-[900px] mx-auto">
        <div className="flex items-center justify-center gap-3 mb-6 max-md:mb-4">
          <h2 className="text-2xl font-bold text-zinc-950 dark:text-zinc-100 max-md:text-lg">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="2" className="inline mr-2 -mt-1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
            관심종목
          </h2>
          <PushToggle watchlistCodes={watchlist.map((w) => w.code)} />
        </div>

        {/* 변동 요약 배너 — 누적 변동이 SCORE_DELTA_ALERT_THRESHOLD 이상인 종목만 표시 */}
        {alertedStocks.length > 0 && (
          <div
            role="alert"
            aria-label={`관심종목 ${alertedStocks.length}개 점수 변동`}
            className="mb-6 rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 max-md:px-3"
          >
            <div className="flex items-center gap-2 mb-2.5">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#f59e0b"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                관심종목 {alertedStocks.length}개 점수 변동
              </span>
            </div>
            <ul
              className="flex flex-wrap gap-2"
              aria-label="점수 변동 종목 목록"
            >
              {alertedStocks.map((s) => {
                const delta = s.score! - s.baseScore!;
                const isUp = delta > 0;
                return (
                  <li key={s.code}>
                    <button
                      onClick={() => handleAnalyze(s.name, 5)}
                      aria-label={`${s.name} 점수 ${isUp ? "상승" : "하락"} ${Math.abs(delta)}점, 분석 보기`}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-colors border ${
                        isUp
                          ? "bg-green-50 dark:bg-green-950/60 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/60"
                          : "bg-red-50 dark:bg-red-950/60 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/60"
                      }`}
                    >
                      <span>{s.name}</span>
                      <span aria-hidden="true">
                        {isUp ? `▲+${delta}` : `▼${delta}`}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-4 gap-3 max-md:grid-cols-2">
          {watchlist.map((stock) => {
            // 누적 변동: 담은 시점(baseScore) 대비 현재 점수
            const baseDelta =
              stock.baseScore != null && stock.score != null && stock.score !== stock.baseScore
                ? stock.score - stock.baseScore
                : null;
            // 직전 변동: prevScore 기반 — hover title 보조용
            const prevDelta =
              stock.prevScore != null && stock.score != null && stock.score !== stock.prevScore
                ? stock.score - stock.prevScore
                : null;
            // 서버 점수 히스토리 (Cycle 92) — 2회 이상 관측부터 스파크라인 표시
            const history = scoreHistory[stock.code];
            return (
            <div key={stock.code} className="group relative">
              <button
                onClick={() => handleAnalyze(stock.name, 5)}
                className="w-full flex items-center gap-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4 text-left cursor-pointer transition-all hover:border-amber-500 hover:shadow-md max-md:p-3"
              >
                <div className="w-9 h-9 bg-amber-50 dark:bg-amber-950 rounded-lg flex items-center justify-center shrink-0">
                  {stock.score != null ? (
                    <span className="text-amber-600 font-bold text-xs">{stock.score}</span>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-zinc-950 dark:text-zinc-100 flex items-center gap-1.5 flex-wrap">
                    <span className="truncate">{stock.name}</span>
                    {stock.grade && <span className="text-xs font-normal text-amber-500 shrink-0">{stock.grade}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                    <span className="text-xs text-zinc-400">{stock.code} · {stock.market}</span>
                    {baseDelta !== null && (
                      <span
                        className={`text-[10px] font-bold px-1 py-0.5 rounded leading-none shrink-0 ${
                          baseDelta > 0
                            ? "bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400"
                            : "bg-red-50 dark:bg-red-950 text-red-500 dark:text-red-400"
                        }`}
                        aria-label={`담은 뒤 누적 변동 ${baseDelta > 0 ? "상승" : "하락"} ${Math.abs(baseDelta)}점`}
                        title={prevDelta !== null ? `직전 변동 ${prevDelta > 0 ? "+" : ""}${prevDelta}점` : undefined}
                      >
                        {baseDelta > 0 ? `▲+${baseDelta}` : `▼${baseDelta}`}
                      </span>
                    )}
                  </div>
                </div>
                {history && history.length >= 2 && (
                  <span className="shrink-0 max-md:hidden">
                    <ScoreSparkline points={history} />
                  </span>
                )}
              </button>
              <button
                onClick={() => removeStock(stock.code)}
                className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-zinc-200 dark:bg-zinc-700 text-zinc-500 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 max-md:opacity-100 transition-opacity hover:bg-red-500 hover:text-white cursor-pointer"
              >×</button>
            </div>
            );
          })}
        </div>
      </div>
    </section>
    )}

    {/* 인기 종목 바로가기 */}
    <section className="py-12 px-5 max-md:py-8">
      <div className="max-w-[900px] mx-auto">
        <h2 className="text-2xl font-bold text-zinc-950 dark:text-zinc-100 text-center mb-8 max-md:text-lg max-md:mb-5">인기 종목</h2>
        <div className="grid grid-cols-4 gap-3 max-md:grid-cols-2">
          {popular.map((stock) => (
            <button
              key={stock.code}
              onClick={() => handleAnalyze(stock.name, 5)}
              className="flex items-center gap-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4 text-left cursor-pointer transition-all hover:border-indigo-500 hover:shadow-md max-md:p-3"
            >
              <div className="w-9 h-9 bg-indigo-50 dark:bg-indigo-950 rounded-lg flex items-center justify-center shrink-0">
                {stock.score ? (
                  <span className="text-indigo-500 font-bold text-xs">{Math.round(stock.score)}</span>
                ) : (
                  <span className="text-indigo-500 font-bold text-xs">FIN</span>
                )}
              </div>
              <div>
                <div className="text-sm font-semibold text-zinc-950 dark:text-zinc-100">
                  {stock.name}
                  {stock.grade && <span className="ml-1.5 text-xs font-normal text-indigo-500">{stock.grade}</span>}
                </div>
                <div className="text-xs text-zinc-400">{stock.code} · {stock.market}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
    </PageTransition>
  );
}
